import { useState } from 'react';
import { Cookie, X } from 'lucide-react';
import { LegalModal, hasConsent, saveConsentDetailed } from '@/components/legal/LegalModal';
import { useTranslation } from 'react-i18next';

// Banner conforme a la guia AEPD 2023:
// - "Rechazar todas" igual de visible que "Aceptar todas" (mismo tamano y posicion)
// - Configuracion granular accesible desde el banner inicial
// - Cookies no esenciales NO se cargan hasta el opt-in (GA gateado en main.tsx via window.funcomunionConsentAnalytics)
// - "Seguir navegando" NO equivale a consentir
// - Permite retirar consentimiento desde el footer en cualquier momento
export function CookieBanner() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(!hasConsent());
  const [showPolicy, setShowPolicy] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  const acceptAll = () => {
    saveConsentDetailed({ analytics: true, marketing: true });
    enableAnalyticsIfChosen(true);
    setVisible(false);
  };
  const rejectAll = () => {
    saveConsentDetailed({ analytics: false, marketing: false });
    setVisible(false);
  };
  const saveCustom = () => {
    saveConsentDetailed({ analytics, marketing });
    enableAnalyticsIfChosen(analytics);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <>
      <div
        className="fixed bottom-0 left-0 right-0 z-[80] bg-white border-t border-[#D4AF37]/20 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]"
        data-no-prerender
        role="region"
        aria-label={t('home.cookieSettingsTitle')}
      >
        <div className="container mx-auto px-4 sm:px-6 py-4">
          {!showSettings ? (
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <Cookie className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" aria-hidden="true" />
                <p className="text-sm text-[#374151]">
                  {t('home.cookieIntro')}{' '}
                  <button type="button" onClick={() => setShowPolicy(true)} className="text-[#7A5F0A] underline hover:no-underline">{t('home.cookiePolicyLink')}</button>
                </p>
              </div>
              {/* AEPD 2023: los tres botones tienen visualmente la misma jerarquia */}
              <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto">
                <button
                  type="button"
                  onClick={rejectAll}
                  className="flex-1 sm:flex-initial px-4 py-2 text-sm rounded-full border border-gray-300 text-[#374151] hover:bg-gray-50 transition-colors whitespace-nowrap font-medium"
                >
                  {t('home.cookieRejectAll')}
                </button>
                <button
                  type="button"
                  onClick={() => setShowSettings(true)}
                  className="flex-1 sm:flex-initial px-4 py-2 text-sm rounded-full border border-gray-300 text-[#374151] hover:bg-gray-50 transition-colors whitespace-nowrap font-medium"
                >
                  {t('home.cookieConfigure')}
                </button>
                <button
                  type="button"
                  onClick={acceptAll}
                  className="flex-1 sm:flex-initial px-4 py-2 text-sm rounded-full bg-[#1E3A5F] text-white hover:bg-[#15304F] transition-colors whitespace-nowrap font-medium"
                >
                  {t('home.cookieAcceptAll')}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-[#1E3A5F]">{t('home.cookieSettingsTitle')}</h4>
                <button type="button" onClick={() => setShowSettings(false)} className="p-1 text-gray-400 hover:text-gray-600" aria-label={t('home.cookieClose')}>
                  <X className="w-4 h-4" aria-hidden="true" />
                </button>
              </div>
              <div className="space-y-2 text-sm">
                <label className="flex items-start gap-3 p-2 rounded bg-gray-50">
                  <input type="checkbox" checked disabled className="mt-1 w-4 h-4 accent-[#D4AF37]" />
                  <div className="flex-1">
                    <div className="font-medium text-[#1E3A5F]">{t('home.cookieNecessary')} <span className="text-[10px] text-gray-500">{t('home.cookieNecessaryRequired')}</span></div>
                    <div className="text-xs text-[#6B7280]">{t('home.cookieNecessaryDesc')}</div>
                  </div>
                </label>
                <label className="flex items-start gap-3 p-2 rounded hover:bg-gray-50 cursor-pointer">
                  <input type="checkbox" checked={analytics} onChange={e => setAnalytics(e.target.checked)} className="mt-1 w-4 h-4 accent-[#D4AF37]" />
                  <div className="flex-1">
                    <div className="font-medium text-[#1E3A5F]">{t('home.cookieAnalytics')}</div>
                    <div className="text-xs text-[#6B7280]">{t('home.cookieAnalyticsDesc')}</div>
                  </div>
                </label>
                <label className="flex items-start gap-3 p-2 rounded hover:bg-gray-50 cursor-pointer">
                  <input type="checkbox" checked={marketing} onChange={e => setMarketing(e.target.checked)} className="mt-1 w-4 h-4 accent-[#D4AF37]" />
                  <div className="flex-1">
                    <div className="font-medium text-[#1E3A5F]">{t('home.cookieMarketing')}</div>
                    <div className="text-xs text-[#6B7280]">{t('home.cookieMarketingDesc')}</div>
                  </div>
                </label>
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
                <button onClick={rejectAll} className="px-4 py-2 text-sm rounded-full border border-gray-300 text-[#374151] hover:bg-gray-50">{t('home.cookieRejectAll')}</button>
                <button onClick={saveCustom} className="px-4 py-2 text-sm rounded-full bg-[#1E3A5F] text-white hover:bg-[#15304F] font-medium">{t('home.cookieSaveCustom')}</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {showPolicy && (
        <LegalModal tipo="cookies" onClose={() => setShowPolicy(false)} />
      )}
    </>
  );
}

// Activa GA4 si el usuario consintio analiticas. La libreria de gtag se carga
// siempre (es liviana) pero no envia eventos hasta que se llama gtag('config').
// Si el usuario rechaza, no llamamos config y por tanto no se envia nada.
function enableAnalyticsIfChosen(allowed: boolean) {
  if (!allowed || typeof window === 'undefined') return;
  const w = window as any;
  if (typeof w.gtag === 'function') {
    w.gtag('consent', 'update', { analytics_storage: 'granted' });
  }
}
